import { useEffect, useState } from "react";
import PublicLayout from "@/layouts/PublicLayout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;
import { Rocket, ArrowRight, Calendar, MapPin, Plus, X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function SubmitOpportunityModal({ categories, onClose }) {
  const [form, setForm] = useState({
    title: "", description: "", category: "financements", organization: "",
    contact_name: "", contact_email: "", contact_phone: "", deadline: "", url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
      return toast.error("Merci de remplir les champs obligatoires");
    }
    setSubmitting(true);
    try {
      await api.post("/public/opportunities/submit", form);
      setSuccess(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="submit-opportunity-modal">
        {success ? (
          <div className="p-10 text-center">
            <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
            <h3 className="mt-4 font-serif text-3xl text-navy">Merci !</h3>
            <p className="mt-2 text-neutral-600">Votre opportunité a bien été reçue. Notre équipe va l&apos;examiner et la publier sur La WineTech si elle correspond aux critères de qualité.</p>
            <p className="mt-1 text-sm text-neutral-500">Un email de confirmation vous a été envoyé.</p>
            <button onClick={onClose} className="mt-6 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-wine-600" data-testid="submit-opportunity-success-close">Fermer</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-neutral-100 bg-white/95 p-5 backdrop-blur">
              <div>
                <h3 className="font-serif text-2xl text-navy"><Rocket className="mr-2 inline h-5 w-5 text-wine-500" />Soumettre une opportunité</h3>
                <p className="mt-1 text-xs text-neutral-500">Partagez un appel à projet, un challenge ou une opportunité avec la communauté WineTech.</p>
              </div>
              <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-3 p-5">
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Titre de l&apos;opportunité *</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. AAP Région Occitanie - Innovation viticole 2026" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-title" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Catégorie *</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-category">
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Description détaillée *</label>
                <textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Décrivez l'opportunité, qui peut candidater, les bénéfices, les critères d'éligibilité..." className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-description" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Date limite (si applicable)</label>
                  <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-deadline" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Lien officiel (URL)</label>
                  <input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-url" />
                </div>
              </div>

              <hr className="my-4" />
              <h4 className="font-bold text-navy">Vos coordonnées</h4>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Organisation</label>
                <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Nom de votre structure" className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-organization" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Nom complet *</label>
                  <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-contact-name" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Email *</label>
                  <input required type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-contact-email" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Téléphone (facultatif)</label>
                <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="submit-contact-phone" />
              </div>
              <p className="text-[11px] text-neutral-400 italic">Votre soumission sera examinée par notre équipe avant publication. Vous recevrez un email de confirmation.</p>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-2 border-t border-neutral-100 bg-white p-5">
              <button type="button" onClick={onClose} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" disabled={submitting} className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="submit-opportunity-confirm">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Soumettre
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function OpportunitiesList() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [submitOpen, setSubmitOpen] = useState(false);

  useEffect(() => {
    api.get("/opportunities/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/opportunities").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const filtered = filter === "all" ? items : items.filter((o) => o.category === filter);
  const countByCat = (id) => items.filter((o) => o.category === id).length;

  return (
    <PublicLayout>
      <section className="relative overflow-hidden bg-gradient-to-br from-wine-50 via-white to-amber-50">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-wine-700">
            <Rocket className="h-3.5 w-3.5" /> Opportunités
          </div>
          <h1 className="mt-3 font-serif text-4xl text-navy md:text-6xl">Saisissez votre prochaine opportunité 🚀</h1>
          <p className="mt-4 max-w-3xl text-lg text-neutral-600">Appels à projets, programmes d&apos;accélération, challenges open innovation, missions export, consultations publiques et recherche de partenaires — concentrés en un seul endroit pour la WineTech.</p>
          <div className="mt-6">
            <button onClick={() => setSubmitOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-wine-200/50 hover:bg-wine-600 hover:scale-105 transition-all" data-testid="submit-opportunity-btn">
              <Plus className="h-4 w-4" /> Soumettre une opportunité
            </button>
            <p className="mt-2 text-xs text-neutral-500">Vous avez connaissance d&apos;un appel à projet ou d&apos;une opportunité pertinente ? Partagez-la avec la communauté !</p>
          </div>
        </div>
      </section>

      {submitOpen && <SubmitOpportunityModal categories={categories} onClose={() => setSubmitOpen(false)} />}

      <section className="mx-auto max-w-7xl px-6 py-10">
        {/* Category filter pills */}
        <div className="flex flex-wrap gap-2" data-testid="opportunities-filters">
          <button
            onClick={() => setFilter("all")}
            className={`rounded-full border-2 px-4 py-1.5 text-sm font-semibold transition-all ${filter === "all" ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-neutral-50"}`}
            data-testid="filter-all"
          >
            Toutes ({items.length})
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className={`rounded-full border-2 px-4 py-1.5 text-sm font-semibold transition-all ${filter === c.id ? "border-wine-500 bg-wine-500 text-white" : "border-neutral-200 bg-white hover:bg-neutral-50"}`}
              style={filter === c.id ? { borderColor: c.color, backgroundColor: c.color } : undefined}
              data-testid={`filter-${c.id}`}
            >
              {c.icon} {c.label} ({countByCat(c.id)})
            </button>
          ))}
        </div>

        {filter === "all" && (
          <div className="mt-8 grid gap-3 md:grid-cols-3" data-testid="categories-overview">
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className="flex items-start gap-3 rounded-2xl border border-neutral-100 bg-white p-4 text-left transition-all hover:shadow-md"
                data-testid={`cat-card-${c.id}`}
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl" style={{ backgroundColor: `${c.color}15`, color: c.color }}>{c.icon}</div>
                <div className="min-w-0">
                  <div className="font-bold text-navy">{c.label}</div>
                  <div className="text-xs text-neutral-500">{c.subtitle}</div>
                  <div className="mt-1 text-[11px] font-semibold" style={{ color: c.color }}>{countByCat(c.id)} opportunité{countByCat(c.id) > 1 ? "s" : ""}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Items grid */}
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="opportunities-grid">
          {filtered.length === 0 && (
            <div className="md:col-span-2 lg:col-span-3 rounded-2xl border-2 border-dashed border-neutral-200 p-12 text-center">
              <p className="text-lg font-semibold text-neutral-500">Aucune opportunité pour cette rubrique pour le moment.</p>
              <p className="mt-1 text-sm text-neutral-400">Revenez bientôt — de nouvelles opportunités sont publiées régulièrement.</p>
            </div>
          )}
          {filtered.map((o) => {
            const cat = categories.find((c) => c.id === o.category);
            return (
              <Link
                key={o.id}
                to={`/opportunites/${o.slug}`}
                className="group block overflow-hidden rounded-3xl border border-neutral-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                data-testid={`opportunity-card-${o.id}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-neutral-50">
                  {o.image ? (
                    <img src={resolveUrl(o.image)} alt={o.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="grid h-full w-full place-items-center text-6xl" style={{ backgroundColor: `${cat?.color || "#9C2C44"}15` }}>{cat?.icon || "🚀"}</div>
                  )}
                  {cat && (
                    <span className="absolute top-3 left-3 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold backdrop-blur" style={{ color: cat.color }}>
                      {cat.icon} {cat.label}
                    </span>
                  )}
                  {(o.ticket_price || 0) > 0 && (
                    <span className="absolute top-3 right-3 rounded-full bg-wine-500 px-2.5 py-1 text-xs font-bold text-white">{o.ticket_price} €</span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl font-semibold text-navy line-clamp-2">{o.title}</h3>
                  {o.description && <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{o.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-neutral-500">
                    {o.deadline && (<span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {(o.deadline || "").slice(0, 10)}</span>)}
                    {o.location && (<span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {o.location}</span>)}
                  </div>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-wine-700 group-hover:gap-2 transition-all">En savoir plus <ArrowRight className="h-3.5 w-3.5" /></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </PublicLayout>
  );
}
